import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import Page from '../../layout/Page/Page';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../components/bootstrap/Card';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Input from '../../components/bootstrap/forms/Input';
import Button from '../../components/bootstrap/Button';
import Icon from '../../components/icon/Icon';
import TableSpinner from '../../common/table/TableSpinner';
import showNotification from '../../components/extras/showNotification';
import { selectLoginResponse } from '../../redux/login/login.selector';
import { getOnDutyReportStart, getAllEmployeesCheckInOutStart } from '../../redux/NewReport/NewReport.actions';
import { selectOnDutyReportResponse, selectAllEmployeesCheckInOutResponse } from '../../redux/NewReport/NewReport.selectors';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '../../components/bootstrap/Modal';

interface OnDutyRecord {
	employeeID: number;
	empID: string;
	employeeName: string;
	onDutyFrom: string;
	onDutyTo: string;
	ondutyReason: string | null;
	checkInTime: string | null;
	checkOutTime: string | null;
	reportDate: string;
	transferType?: string;
	checkInLatitude?: number | null;
	checkInLongitude?: number | null;
	checkoutLatitude?: number | null;
	checkoutLongitude?: number | null;
}

interface AllEmployeesRecord {
	employeeID: number;
	empID: string;
	employeeName: string;
	attendanceDate: string;
	checkInTime: string | null;
	checkOutTime: string | null;
	transferType?: string;
	ondutyReason?: string | null;
	checkInLatitude?: number | null;
	checkInLongitude?: number | null;
	checkoutLatitude?: number | null;
	checkoutLongitude?: number | null;
	branchName?: string;
}

const OnDutyReportPage = () => {
	const dispatch = useDispatch();
	const loginData = useSelector(selectLoginResponse);
	const reportResponse = useSelector(selectOnDutyReportResponse);
	const allEmployeesResponse = useSelector(selectAllEmployeesCheckInOutResponse);

	const [startDate, setStartDate] = useState<string>('');
	const [endDate, setEndDate] = useState<string>('');
	const [isLoading, setIsLoading] = useState(false);
	const [tableData, setTableData] = useState<OnDutyRecord[]>([]);
	const [allEmployeesData, setAllEmployeesData] = useState<AllEmployeesRecord[]>([]);
	const [filteredEmployeesData, setFilteredEmployeesData] = useState<AllEmployeesRecord[]>([]);
	const [currentTypeLabel, setCurrentTypeLabel] = useState<string>('On Duty');
	const [reportType, setReportType] = useState<string>('OnDuty');
	const [viewMode, setViewMode] = useState<'onduty' | 'allemployees'>('onduty');
	const [employeeFilter, setEmployeeFilter] = useState<string>('All');
	const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
	const [isMapModalOpen, setIsMapModalOpen] = useState(false);
	const [mapCoordinates, setMapCoordinates] = useState<{ lat: number; lng: number } | null>(null);

	const organisationID = useMemo(() => {
		return (
			loginData?.organisationID ||
			loginData?.data?.organisationID ||
			loginData?.payload?.organisationID ||
			loginData?.payload?.data?.organisationID ||
			loginData?.loginResponse?.organisationID ||
			loginData?.loginResponse?.data?.organisationID ||
			null
		);
	}, [loginData]);

	// Set default dates (current month)
	useEffect(() => {
		const today = new Date();
		const iso = today.toISOString().split('T')[0];
		setStartDate(iso);
		setEndDate(iso);
	}, []);

	const triggerReportFetch = (typeOverride?: string) => {
		if (!startDate || !endDate) {
			showNotification('Warning', 'Please select both start and end dates', 'warning');
			return;
		}

		if (!organisationID) {
			showNotification('Error', 'Organisation missing. Please relogin.', 'danger');
			return;
		}

		setIsLoading(true);
		const typeToUse = typeOverride || reportType;

		setCurrentTypeLabel(
			typeToUse === 'Training'
				? 'Training'
				: typeToUse === 'Meeting'
				? 'Meeting'
				: typeToUse === 'All'
				? 'All types'
				: 'On Duty',
		);

		if (viewMode === 'onduty') {
			dispatch(
				getOnDutyReportStart({
					organisationID,
					startDate,
					endDate,
					transferType: typeToUse,
				}),
			);
		} else {
			dispatch(
				getAllEmployeesCheckInOutStart({
					organisationID,
					startDate,
					endDate,
				}),
			);
		}
	};

	const handleFetchReport = () => {
		triggerReportFetch();
	};

	const handleClearDates = () => {
		setStartDate('');
		setEndDate('');
		setTableData([]);
		setIsLoading(false);
	};

	const handleReportTypeChange = (type: string) => {
		setReportType(type);
		triggerReportFetch(type);
	};

	const handleViewModeChange = (mode: 'onduty' | 'allemployees') => {
		setViewMode(mode);
		if (organisationID && startDate && endDate) {
			setIsLoading(true);
			if (mode === 'onduty') {
				dispatch(
					getOnDutyReportStart({
						organisationID,
						startDate,
						endDate,
						transferType: reportType,
					}),
				);
			} else {
				dispatch(
					getAllEmployeesCheckInOutStart({
						organisationID,
						startDate,
						endDate,
					}),
				);
			}
		}
	};

	const handleSort = (key: string) => {
		let direction: 'asc' | 'desc' = 'asc';
		if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
			direction = 'desc';
		}
		setSortConfig({ key, direction });
	};

	const getSortedData = (data: AllEmployeesRecord[]) => {
		if (!sortConfig) return data;
		
		const sorted = [...data].sort((a, b) => {
			let aValue: any = a[sortConfig.key as keyof AllEmployeesRecord];
			let bValue: any = b[sortConfig.key as keyof AllEmployeesRecord];
			
			if (aValue === null || aValue === undefined) aValue = '';
			if (bValue === null || bValue === undefined) bValue = '';
			
			if (typeof aValue === 'string') {
				aValue = aValue.toLowerCase();
				bValue = bValue.toLowerCase();
			}
			
			if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
			if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
			return 0;
		});
		
		return sorted;
	};

	useEffect(() => {
		if (organisationID && startDate && endDate) {
			triggerReportFetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [organisationID, startDate, endDate]);

	// Update filtered data when allEmployeesData or sortConfig changes
	useEffect(() => {
		const sorted = getSortedData(allEmployeesData);
		setFilteredEmployeesData(sorted);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allEmployeesData, sortConfig]);

	const handleExport = () => {
		if (viewMode === 'onduty') {
			if (!tableData.length) {
				showNotification('Warning', 'No data to export', 'warning');
				return;
			}

			const headers = [
				'Employee ID',
				'Employee Code',
				'Employee Name',
				'From',
				'To',
				'Check-In Time',
				'Check-Out Time',
				'Check-In Latitude',
				'Check-Out Latitude',
				'Report Date',
			];

			const csvRows = [headers.join(',')];

			tableData.forEach((row) => {
				csvRows.push(
					[
						row.employeeID,
						`"${row.empID || ''}"`,
						`"${row.employeeName || ''}"`,
						row.onDutyFrom || '',
						row.onDutyTo || '',
						row.checkInTime || '',
						row.checkOutTime || '',
						row.checkInLatitude != null ? row.checkInLatitude : '',
						row.checkoutLatitude != null ? row.checkoutLatitude : '',
						row.reportDate || startDate,
					].join(','),
				);
			});

			const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			const typeLabel = reportType === 'All' ? 'AllTypes' : reportType || 'OnDuty';
			link.download = `OnDuty_Report_${typeLabel}_${startDate}_to_${endDate}.csv`;
			link.click();
			window.URL.revokeObjectURL(url);
		} else {
			if (!filteredEmployeesData.length) {
				showNotification('Warning', 'No data to export', 'warning');
				return;
			}

			const headers = [
				'Employee ID',
				'Employee Code',
				'Employee Name',
				'Date',
				'Branch',
				'Type',
				'Check-In Time',
				'Check-Out Time',
				'Check-In Latitude',
				'Check-Out Latitude',
			];

			const csvRows = [headers.join(',')];

			filteredEmployeesData.forEach((row) => {
				csvRows.push(
					[
						row.employeeID,
						`"${row.empID || ''}"`,
						`"${row.employeeName || ''}"`,
						row.attendanceDate || '',
						`"${row.branchName || ''}"`,
						row.transferType || 'Regular',
						row.checkInTime || '',
						row.checkOutTime || '',
						row.checkInLatitude != null ? row.checkInLatitude : '',
						row.checkoutLatitude != null ? row.checkoutLatitude : '',
					].join(','),
				);
			});

			const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			const filterLabel = employeeFilter === 'All' ? 'All' : employeeFilter || 'All';
			link.download = `AllEmployees_CheckInOut_${filterLabel}_${startDate}_to_${endDate}.csv`;
			link.click();
			window.URL.revokeObjectURL(url);
		}
	};

	const formatTime = (value: string | null) => {
		if (!value) return '-';
		return value.length === 5 ? `${value}:00` : value;
	};

	const openMapWithCoordinates = (latitude: number, longitude: number) => {
		setMapCoordinates({ lat: latitude, lng: longitude });
		setIsMapModalOpen(true);
	};

	const renderCheckInTime = (row: OnDutyRecord | AllEmployeesRecord) => {
		const time = formatTime(row.checkInTime);
		
		// Convert to numbers and check if latitude and longitude are present and valid
		const lat = row.checkInLatitude != null ? Number(row.checkInLatitude) : null;
		const lng = row.checkInLongitude != null ? Number(row.checkInLongitude) : null;
		
		// Check if both are valid numbers and not zero (valid coordinates)
		if (
			lat != null &&
			lng != null &&
			!isNaN(lat) &&
			!isNaN(lng) &&
			lat !== 0 &&
			lng !== 0
		) {
			return (
				<a
					href="#"
					onClick={(e) => {
						e.preventDefault();
						openMapWithCoordinates(lat, lng);
					}}
					style={{
						color: '#0d6efd',
						textDecoration: 'underline',
						cursor: 'pointer',
					}}
					title={`View location on map (${lat}, ${lng})`}
				>
					{time}
				</a>
			);
		}
		
		return <span>{time}</span>;
	};

	const renderCheckOutTime = (row: OnDutyRecord | AllEmployeesRecord) => {
		const time = formatTime(row.checkOutTime);
		
		// Convert to numbers and check if latitude and longitude are present and valid
		const lat = row.checkoutLatitude != null ? Number(row.checkoutLatitude) : null;
		const lng = row.checkoutLongitude != null ? Number(row.checkoutLongitude) : null;
		
		// Check if both are valid numbers and not zero (valid coordinates)
		if (
			lat != null &&
			lng != null &&
			!isNaN(lat) &&
			!isNaN(lng) &&
			lat !== 0 &&
			lng !== 0
		) {
			return (
				<a
					href="#"
					onClick={(e) => {
						e.preventDefault();
						openMapWithCoordinates(lat, lng);
					}}
					style={{
						color: '#0d6efd',
						textDecoration: 'underline',
						cursor: 'pointer',
					}}
					title={`View location on map (${lat}, ${lng})`}
				>
					{time}
				</a>
			);
		}
		
		return <span>{time}</span>;
	};

	useEffect(() => {
		if (!reportResponse) return;

		setIsLoading(false);

		if (reportResponse.status === 'success') {
			const rows = Array.isArray(reportResponse.data) ? reportResponse.data : [];
			
			// Debug: Log first row to check for latitude/longitude fields
			if (rows.length > 0) {
				if (process.env.NODE_ENV === 'development') console.log('OnDuty Report - First row sample:', rows[0]);
				if (process.env.NODE_ENV === 'development') console.log('checkInLatitude:', rows[0].checkInLatitude);
				if (process.env.NODE_ENV === 'development') console.log('checkInLongitude:', rows[0].checkInLongitude);
			}
			
			setTableData(rows);

			if (!rows.length) {
				showNotification('Warning', `No ${currentTypeLabel} records found`, 'warning');
			}
		} else {
			setTableData([]);
			const rawMessage =
				reportResponse.message ||
				reportResponse.message_text ||
				reportResponse.error ||
				'Unable to fetch On Duty report';
			const message =
				typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
			showNotification('Error', message, 'danger');
		}
	}, [reportResponse]);

	useEffect(() => {
		if (!allEmployeesResponse) return;

		setIsLoading(false);

		if (allEmployeesResponse.status === 'success') {
			const rows = Array.isArray(allEmployeesResponse.data) ? allEmployeesResponse.data : [];
			setAllEmployeesData(rows);
			setFilteredEmployeesData(rows);

			if (!rows.length) {
				showNotification('Warning', 'No employee records found', 'warning');
			}
		} else {
			setAllEmployeesData([]);
			setFilteredEmployeesData([]);
			const rawMessage =
				allEmployeesResponse.message ||
				allEmployeesResponse.message_text ||
				allEmployeesResponse.error ||
				'Unable to fetch all employees data';
			const message =
				typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
			showNotification('Error', message, 'danger');
		}
	}, [allEmployeesResponse]);

	return (
		<PageWrapper title='On Duty Report'>
			<SubHeader>
				<SubHeaderLeft>
					<Icon icon='DirectionsWalk' className='me-2' size='2x' />
					<span className='h4 mb-0'>On Duty Report</span>
				</SubHeaderLeft>
			</SubHeader>
			<Page container='fluid' className='p-0'>
				<div className='d-flex flex-column' style={{ height: 'calc(100vh - 120px)' }}>
					<Card className='mb-2'>
						<CardBody className='py-3'>
							<div className='row g-3 align-items-end'>
								<div className='col-md-2'>
									<FormGroup id='viewMode' label='View Mode' isFloating>
										<select
											className='form-select'
											value={viewMode}
											onChange={(e) => handleViewModeChange(e.target.value as 'onduty' | 'allemployees')}
											disabled={isLoading}>
											<option value='onduty'>On Duty Report</option>
											<option value='allemployees'>All Employees</option>
										</select>
									</FormGroup>
								</div>
								<div className='col-md-3'>
									<FormGroup id='startDate' label='From Date' isFloating>
										<Input
											type='date'
											name='startDate'
											value={startDate}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setStartDate(e.target.value)
											}
										/>
									</FormGroup>
								</div>
								<div className='col-md-3'>
									<FormGroup id='endDate' label='To Date' isFloating>
										<Input
											type='date'
											name='endDate'
											value={endDate}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setEndDate(e.target.value)
											}
										/>
									</FormGroup>
								</div>
								<div className='col-md-4 text-end'>
									<Button
										color='primary'
										className='me-2'
										icon={isLoading ? undefined : 'Search'}
										isDisable={isLoading}
										onClick={handleFetchReport}>
										{isLoading ? 'Fetching...' : 'Fetch Report'}
									</Button>
									<Button
										color='secondary'
										className='me-2'
										icon='RestartAlt'
										onClick={handleClearDates}
										isDisable={isLoading}>
										Clear Dates
									</Button>
									<Button
										color='success'
										icon='FileDownload'
										onClick={handleExport}
										isDisable={viewMode === 'onduty' ? !tableData.length : !filteredEmployeesData.length}>
										Export CSV
									</Button>
								</div>
							</div>
						</CardBody>
					</Card>

					<Card className='flex-fill d-flex flex-column mb-0' style={{ minHeight: 0 }}>
						<CardHeader className='py-2'>
							<CardLabel>
								<CardTitle>Summary</CardTitle>
							</CardLabel>
							<div className='d-flex flex-column flex-md-row align-items-md-center gap-2 ms-auto'>
								{viewMode === 'onduty' && (
									<div className='d-flex align-items-center flex-wrap gap-2'>
										<span className='text-muted small'>Filter:</span>
										{[
											{ label: 'On Duty', value: 'OnDuty' },
											{ label: 'Training', value: 'Training' },
											{ label: 'Meeting', value: 'Meeting' },
											{ label: 'All', value: 'All' },
										].map((option) => (
											<Button
												key={option.value}
												color='primary'
												isOutline={reportType !== option.value}
												size='sm'
												className='px-3'
												onClick={() => handleReportTypeChange(option.value)}>
												{option.label}
											</Button>
										))}
									</div>
								)}
								<div className='text-end ms-md-3'>
									<strong>Total:</strong> {viewMode === 'onduty' ? tableData.length : filteredEmployeesData.length}
								</div>
							</div>
						</CardHeader>
						<CardBody className='p-0 flex-fill d-flex flex-column' style={{ minHeight: 0, overflow: 'hidden' }}>
							{isLoading ? (
								<div className='p-4 d-flex align-items-center justify-content-center flex-fill'>
									<TableSpinner />
								</div>
							) : viewMode === 'onduty' ? (
								tableData.length === 0 ? (
									<div className='text-center py-4 text-muted d-flex align-items-center justify-content-center flex-fill'>
										No {currentTypeLabel} records found
									</div>
								) : (
									<div className='table-responsive flex-fill' style={{ overflowY: 'auto', height: '100%' }}>
										<table className='table table-modern table-striped table-hover mb-0'>
											<thead className='table-light sticky-top'>
												<tr>
													<th>#</th>
													<th>Employee Code</th>
													<th>Employee Name</th>
													<th>From</th>
													<th>To</th>
													<th>Check-In Time</th>
													<th>Check-Out Time</th>
												</tr>
											</thead>
											<tbody>
												{tableData.map((row, index) => (
													<tr key={`${row.employeeID}-${index}`}>
														<td>{index + 1}</td>
														<td>{row.empID || '-'}</td>
														<td>{row.employeeName || '-'}</td>
														<td>{row.onDutyFrom || '-'}</td>
														<td>{row.onDutyTo || '-'}</td>
														<td>{renderCheckInTime(row)}</td>
														<td>{renderCheckOutTime(row)}</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)
							) : (
								filteredEmployeesData.length === 0 ? (
									<div className='text-center py-4 text-muted d-flex align-items-center justify-content-center flex-fill'>
										No employee records found
									</div>
								) : (
									<div className='table-responsive flex-fill' style={{ overflowY: 'auto', height: '100%' }}>
										<table className='table table-modern table-striped table-hover mb-0'>
											<thead className='table-light sticky-top'>
												<tr>
													<th>#</th>
													<th>Employee Code</th>
													<th>Employee Name</th>
													<th>Date</th>
													<th
														style={{ cursor: 'pointer', userSelect: 'none' }}
														onClick={() => handleSort('branchName')}
														title='Click to sort by branch'>
														Branch
														{sortConfig?.key === 'branchName' && (
															<Icon
																icon={sortConfig.direction === 'asc' ? 'ArrowUpward' : 'ArrowDownward'}
																size='sm'
																className='ms-1'
															/>
														)}
													</th>
													<th>Type</th>
													<th>Check-In Time</th>
													<th>Check-Out Time</th>
												</tr>
											</thead>
											<tbody>
												{filteredEmployeesData.map((row, index) => (
													<tr key={`${row.employeeID}-${row.attendanceDate}-${index}`}>
														<td>{index + 1}</td>
														<td>{row.empID || '-'}</td>
														<td>{row.employeeName || '-'}</td>
														<td>{row.attendanceDate || '-'}</td>
														<td>{row.branchName || '-'}</td>
														<td>{row.transferType || 'Regular'}</td>
														<td>{renderCheckInTime(row)}</td>
														<td>{renderCheckOutTime(row)}</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)
							)}
						</CardBody>
					</Card>
				</div>
			</Page>
			
			{/* Map Modal */}
			<Modal
				isOpen={isMapModalOpen}
				setIsOpen={setIsMapModalOpen}
				titleId='mapModalTitle'
				size='lg'
				isCentered>
				<ModalHeader setIsOpen={setIsMapModalOpen}>
					<ModalTitle id='mapModalTitle'>Location Map</ModalTitle>
				</ModalHeader>
				<ModalBody>
					{mapCoordinates && (
						<div>
							<div style={{ width: '100%', height: '500px', position: 'relative', marginBottom: '1rem' }}>
								<iframe
									width='100%'
									height='100%'
									style={{ border: 0, borderRadius: '8px' }}
									src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapCoordinates.lng - 0.01},${mapCoordinates.lat - 0.01},${mapCoordinates.lng + 0.01},${mapCoordinates.lat + 0.01}&layer=mapnik&marker=${mapCoordinates.lat},${mapCoordinates.lng}`}
									allowFullScreen
									loading='lazy'
									title='Location map'
								/>
							</div>
							<div className='text-center text-muted small'>
								<strong>Coordinates:</strong> {mapCoordinates.lat.toFixed(6)}, {mapCoordinates.lng.toFixed(6)}
								<br />
								<div className='mt-2 d-flex gap-3 justify-content-center'>
									<a
										href={`https://www.google.com/maps?q=${mapCoordinates.lat},${mapCoordinates.lng}`}
										target='_blank'
										rel='noopener noreferrer'
										className='text-decoration-none'>
										<Icon icon='Map' size='sm' className='me-1' />
										Open in Google Maps
									</a>
									<span className='text-muted'>|</span>
									<a
										href={`https://www.openstreetmap.org/?mlat=${mapCoordinates.lat}&mlon=${mapCoordinates.lng}&zoom=15`}
										target='_blank'
										rel='noopener noreferrer'
										className='text-decoration-none'>
										<Icon icon='Map' size='sm' className='me-1' />
										Open in OpenStreetMap
									</a>
								</div>
							</div>
						</div>
					)}
				</ModalBody>
			</Modal>
		</PageWrapper>
	);
};

export default OnDutyReportPage;

